import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const phoneRegExp = /^[6-9]\d{9}$/;

const onboardingFormSchema = yup.object().shape({
  // Personal Information
  employeeId: yup.string().required('Employee ID is required'),
  employeeName: yup.string().required('Employee name is required'),
  sex: yup.string().required('Sex is required'),
  dateOfJoining: yup.date().required('Date of joining is required'),
  country: yup.string().required('Country is required'),
  nationality: yup.string().required('Nationality is required'),
  dateOfBirth: yup.date().required('Date of birth is required'),
  maritalStatus: yup.string().required('Marital status is required'),
  bloodGroup: yup.string().required('Blood group is required'),
  reportingManager: yup.string().required('Reporting Manager is required'),
  unitHead: yup.string().required('Unit Head is required'),
  currentAddress: yup.object().shape({
    street: yup.string().required('Street address is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zipCode: yup
      .string()
      .matches(/^\d{6}$/, 'ZIP code must be a 6-digit number')
      .required('ZIP code is required'),
    country: yup.string().required('Country is required'),
  }),
  permanentAddress: yup.object().shape({
    sameAsCurrent: yup.boolean(),
    street: yup.string().when('sameAsCurrent', {
      is: false,
      then: () => yup.string().required('Street address is required'),
      otherwise: () => yup.string(),
    }),
    city: yup.string().when('sameAsCurrent', {
      is: false,
      then: () => yup.string().required('City is required'),
      otherwise: () => yup.string(),
    }),
    state: yup.string().when('sameAsCurrent', {
      is: false,
      then: () => yup.string().required('State is required'),
      otherwise: () => yup.string(),
    }),
    zipCode: yup.string().when('sameAsCurrent', {
      is: false,
      then: () =>
        yup
          .string()
          .matches(/^\d{6}$/, 'ZIP code must be a 6-digit number')
          .required('ZIP code is required'),
      otherwise: () => yup.string(),
    }),
    country: yup.string().when('sameAsCurrent', {
      is: false,
      then: () => yup.string().required('Country is required'),
      otherwise: () => yup.string(),
    }),
  }),
  emailId: yup.string().email('Invalid email').required('Email is required'),
  
  // India specific documents (with validation)
  panCardNumber: yup.string().when('country', {
    is: 'India',
    then: () => yup
    .string()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'PAN must be in the format ABCDE1234F')
    .required('PAN card number is required'),
    otherwise: () => yup.string().optional(),
  }),
  aadharNumber: yup.string().when('country', {
    is: 'India',
    then: () => yup
    .string()
      .matches(/^[2-9]{1}[0-9]{11}$/, 'Aadhar number must be a 12-digit numeric value starting with 2-9')
    .required('Aadhar number is required'),
    otherwise: () => yup.string().optional(),
  }),
  
  // Indonesia and Vietnam documents (no specific validation)
  taxIdentificationNumber: yup.string().when('country', {
    is: (val) => val === 'Indonesia' || val === 'Vietnam',
    then: () => yup.string().required('Tax Identification Number is required'),
    otherwise: () => yup.string().optional(),
  }),
  nationalIdentityNumber: yup.string().when('country', {
    is: (val) => val === 'Indonesia' || val === 'Vietnam',
    then: () => yup.string().required('National Identity Number is required'),
    otherwise: () => yup.string().optional(),
  }),
  
  mobileNumber: yup.string().required('Mobile number is required'),
  emergencyContact: yup.object().shape({
    name: yup.string().required('Emergency contact name is required'),
    contactNo: yup.string().required('Emergency contact number is required'),
    relationshipWithEmployee: yup.string().required('Relationship with employee is required'),
  }),
  passportNo: yup.string().optional(),
  familyMembers: yup.array().of(
    yup.object().shape({
      name: yup.string(),
      dateOfBirth: yup.date(),
      relation: yup.string(),
      residingWithSelf: yup.boolean(),
    })
  ),
  workExperiences: yup.array().of(
    yup.object().shape({
      organizationName: yup.string(),
      designation: yup.string(),
      fromDate: yup.date(),
      toDate: yup.date(),
      contactNumber: yup.string(),
      jobDescription: yup.string(),
    })
  ),
  bankDetails: yup.object().shape({
    bankName: yup.string().required('Bank name is required'),
    branchAddress: yup.string().required('Branch address is required'),
    accountName: yup.string().required('Account name is required'),
    accountNo: yup.string().required('Account number is required'),
    // India specific
    ifscCode: yup.string().when('country', {
      is: 'India',
      then: () => yup
      .string()
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC code must be in the format ABCD0123456')
      .required('IFSC code is required'),
      otherwise: () => yup.string().optional(),
    }),
    // Indonesia and Vietnam specific
    swiftCode: yup.string().when('country', {
      is: (val) => val === 'Indonesia' || val === 'Vietnam',
      then: () => yup.string().required('Swift/Bank code is required'),
      otherwise: () => yup.string().optional(),
    }),
  }),
  pfDetails: yup.object().shape({
    // India specific
    uanNumber: yup.string().when('country', {
      is: 'India',
      then: () => yup
        .string()
        .matches(/^[0-9]{12}$/, 'UAN number must be a 12-digit numeric value')
        .optional(),
      otherwise: () => yup.string().optional(),
    }),
    // Indonesia and Vietnam - unified social security number
    socialSecurityNumber: yup.string().when('country', {
      is: (val) => val === 'Indonesia' || val === 'Vietnam',
      then: () => yup.string().optional(),
      otherwise: () => yup.string().optional(),
    }),
  }),
  declaration: yup.boolean().oneOf([true], 'You must accept the declaration to proceed').required('Declaration is required'),
});

const FormsStep = ({ targetUserId, onFormSubmit }) => {
  const { user, getAuthHeaders } = useAuth();
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // Use targetUserId if provided, otherwise use current user's ID
  const userIdToUse = targetUserId || user?.id;

  const formik = useFormik({
    initialValues: {
      // Personal Information
      employeeId: '',
      employeeName: '',
      sex: '',
      dateOfJoining: '',
      country: '',
      nationality: '',
      dateOfBirth: '',
      maritalStatus: '',
      bloodGroup: '',
      reportingManager: '',
      unitHead: '',
      currentAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      permanentAddress: {
        sameAsCurrent: false,
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      emailId: '',
      
      // India specific documents (with validation)
      panCardNumber: '',
      aadharNumber: '',
      
      // Indonesia and Vietnam documents (no specific validation)
      taxIdentificationNumber: '',
      nationalIdentityNumber: '',
      
      mobileNumber: '',
      emergencyContact: {
        name: '',
        contactNo: '',
        relationshipWithEmployee: '',
      },
      passportNo: '',
      familyMembers: [{
        name: '',
        dateOfBirth: '',
        relation: '',
        residingWithSelf: false,
      }],
      workExperiences: [{
        organizationName: '',
        designation: '',
        fromDate: '',
        toDate: '',
        contactNumber: '',
        jobDescription: '',
      }],
      bankDetails: {
        bankName: '',
        branchAddress: '',
        accountName: '',
        accountNo: '',
        // India specific
        ifscCode: '',
        // Indonesia and Vietnam specific
        swiftCode: '',
      },
              pfDetails: {
          // India specific
          uanNumber: '',
          // Indonesia and Vietnam - unified social security number
          socialSecurityNumber: '',
        },
      declaration: false,
    },
    validationSchema: onboardingFormSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setHasSubmitted(true); // Set flag for scroll-to-error
      setLoading(true);
      try {
        // Filter out empty work experiences (only if they have at least one field filled)
        const filteredWorkExperiences = values.workExperiences?.filter(exp => 
          (exp.organizationName && exp.organizationName.trim() !== '') ||
          (exp.designation && exp.designation.trim() !== '') ||
          exp.fromDate ||
          exp.toDate ||
          (exp.contactNumber && exp.contactNumber.trim() !== '') ||
          (exp.jobDescription && exp.jobDescription.trim() !== '')
        ) || [];

        // Filter out empty family members (only if they have at least one field filled)
        const filteredFamilyMembers = values.familyMembers?.filter(member => 
          (member.name && member.name.trim() !== '') ||
          member.dateOfBirth ||
          (member.relation && member.relation.trim() !== '')
        ) || [];

        const payload = {
          userId: user.id,
          employeeId: values.employeeId,
          employeeName: values.employeeName,
          sex: values.sex,
          dateOfJoining: values.dateOfJoining,
          country: values.country,
          nationality: values.nationality,
          dateOfBirth: values.dateOfBirth,
          maritalStatus: values.maritalStatus,
          bloodGroup: values.bloodGroup,
          reportingManager: values.reportingManager,
          unitHead: values.unitHead,
          currentAddress: values.currentAddress,
          permanentAddress: values.permanentAddress,
          emailId: values.emailId,
          
          // India specific documents (with validation)
          panCardNumber: values.panCardNumber,
          aadharNumber: values.aadharNumber,
          
          // Indonesia and Vietnam documents (no specific validation)
          taxIdentificationNumber: values.taxIdentificationNumber,
          nationalIdentityNumber: values.nationalIdentityNumber,
          
          mobileNumber: values.mobileNumber,
          emergencyContact: values.emergencyContact,
          passportNo: values.passportNo,
          familyMembers: filteredFamilyMembers,
          workExperiences: filteredWorkExperiences,
          bankDetails: values.bankDetails,
          pfDetails: {
            uanNumber: values.pfDetails.uanNumber,
            socialSecurityNumber: values.pfDetails.socialSecurityNumber,
          },
          declaration: values.declaration,
        };

        const url = isUpdate
          ? `${API_URL}/onboarding/onboarding-form/${userIdToUse}`
          : `${API_URL}/onboarding/onboarding-form`;

        const method = isUpdate ? axios.put : axios.post;

        await method(url, payload, { 
          headers: getAuthHeaders()
        });

        toast.success(`Form data ${isUpdate ? 'updated' : 'saved'} successfully!`);
        setIsUpdate(true);
        // Call the callback if provided (with a small delay to ensure DB update is complete)
        if (onFormSubmit && typeof onFormSubmit === 'function') {
          setTimeout(() => {
            onFormSubmit();
          }, 500);
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to submit form data.');
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, handleChange, handleSubmit, errors, touched, setFieldValue, handleBlur, setValues } = formik;

  useEffect(() => {
    const fetchOnboardingData = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/onboarding/onboarding-form/${userIdToUse}`,
          { 
            headers: getAuthHeaders()
          }
        );

        if (res.data && res.data.data && res.data.data !== null) {
          const data = res.data.data;
          const formattedData = {
            employeeId: data.employeeId || '',
            employeeName: data.employeeName || '',
            sex: data.sex || '',
            dateOfJoining: data.dateOfJoining
              ? new Date(data.dateOfJoining).toISOString().slice(0, 10)
              : '',
            country: data.country || '',
            nationality: data.nationality || '',
            dateOfBirth: data.dateOfBirth
              ? new Date(data.dateOfBirth).toISOString().slice(0, 10)
              : '',
            maritalStatus: data.maritalStatus || '',
            bloodGroup: data.bloodGroup || '',
            reportingManager: data.reportingManager || '',
            unitHead: data.unitHead || '',
            
            // Address with default values
            currentAddress: {
              street: data.currentAddress?.street || '',
              city: data.currentAddress?.city || '',
              state: data.currentAddress?.state || '',
              zipCode: data.currentAddress?.zipCode || '',
              country: data.currentAddress?.country || '',
            },
            permanentAddress: {
              sameAsCurrent: data.permanentAddress?.sameAsCurrent || false,
              street: data.permanentAddress?.street || '',
              city: data.permanentAddress?.city || '',
              state: data.permanentAddress?.state || '',
              zipCode: data.permanentAddress?.zipCode || '',
              country: data.permanentAddress?.country || '',
            },
            
            // Contact Info
            emailId: data.emailId || '',
            
            // India specific documents (with validation)
            panCardNumber: data.panCardNumber || '',
            aadharNumber: data.aadharNumber || '',
            
            // Indonesia and Vietnam documents (no specific validation)
            taxIdentificationNumber: data.taxIdentificationNumber || '',
            nationalIdentityNumber: data.nationalIdentityNumber || '',
            
                         mobileNumber: data.mobileNumber || '',
            emergencyContact: {
              name: data.emergencyContact?.name || '',
              contactNo: data.emergencyContact?.contactNo || '',
              relationshipWithEmployee: data.emergencyContact?.relationshipWithEmployee || '',
            },
             passportNo: data.passportNo || '',
            
            // Work Experiences
            workExperiences: data.workExperiences?.map(exp => ({
              organizationName: exp.organizationName || '',
              designation: exp.designation || '',
              fromDate: exp.fromDate ? new Date(exp.fromDate).toISOString().slice(0, 10) : '',
              toDate: exp.toDate ? new Date(exp.toDate).toISOString().slice(0, 10) : '',
              contactNumber: exp.contactNumber || '',
              jobDescription: exp.jobDescription || '',
            })) || [],
            
            // Family Members
            familyMembers: data.familyMembers?.map(member => ({
              name: member.name || '',
              dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth).toISOString().slice(0, 10) : '',
              relation: member.relation || '',
              residingWithSelf: member.residingWithSelf || false,
            })) || [],
            
            // Bank Details
            bankDetails: {
              bankName: data.bankDetails?.bankName || '',
              branchAddress: data.bankDetails?.branchAddress || '',
              accountName: data.bankDetails?.accountName || '',
              accountNo: data.bankDetails?.accountNo || '',
              // India specific
              ifscCode: data.bankDetails?.ifscCode || '',
              // Indonesia and Vietnam specific
              swiftCode: data.bankDetails?.swiftCode || '',
            },

                          // PF Details
              pfDetails: {
                // India specific
                uanNumber: data.pfDetails?.uanNumber || '',
                // Indonesia and Vietnam - unified social security number
                socialSecurityNumber: data.pfDetails?.socialSecurityNumber || '',
            },

            // Declaration
            declaration: data.declaration === true || data.declaration === 'true' || false,
          };

          setValues(formattedData);
          setIsUpdate(true);
        }
      } catch (error) {
        console.error('Error fetching onboarding form data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    if (userIdToUse) {
      fetchOnboardingData();
    } else {
      setDataLoading(false);
    }
  }, [userIdToUse, getAuthHeaders, setValues]);

  // Add scroll-to-error functionality only on form submission
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  useEffect(() => {
    if (hasSubmitted && Object.keys(errors).length > 0) {
      // Find the first error field
      const firstErrorField = Object.keys(errors)[0];
      
      if (firstErrorField) {
        // Handle nested fields (e.g., currentAddress.street)
        const fieldName = firstErrorField.includes('.') ? firstErrorField : firstErrorField;
        const errorElement = document.querySelector(`[name="${fieldName}"]`);
        
        if (errorElement) {
          // Small delay to ensure the error state is fully rendered
          setTimeout(() => {
          errorElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          errorElement.focus();
          }, 100);
        }
      }
      // Reset the submission flag after scrolling
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Force re-render when country changes to update address field labels
  useEffect(() => {
    // This will trigger a re-render when country changes
    // The conditional labels will automatically update
  }, [values.country]);

  // Ensure values are always defined
  const safeValues = {
    currentAddress: values.currentAddress || { street: '', city: '', state: '', zipCode: '', country: '' },
    permanentAddress: values.permanentAddress || { sameAsCurrent: false, street: '', city: '', state: '', zipCode: '', country: '' },
    emergencyContact: values.emergencyContact || { name: '', contactNo: '', relationshipWithEmployee: '' },
    bankDetails: values.bankDetails || { bankName: '', branchAddress: '', accountName: '', accountNo: '', ifscCode: '', swiftCode: '' },
    pfDetails: values.pfDetails || { uanNumber: '', socialSecurityNumber: '' },
    workExperiences: values.workExperiences || [],
    familyMembers: values.familyMembers || [],
    declaration: values.declaration || false,
  };

  
  return (
    <FormikProvider value={formik}>
      <Paper elevation={3} sx={{ p: 3, m: 2 }}>
        {dataLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #e9ecef', 
              borderRadius: 2,
              borderLeft: '4px solid #1976d2'
            }}>
              <Typography variant="body1" sx={{ 
                color: '#495057', 
                lineHeight: 1.7, 
                fontSize: '14px',
                fontWeight: 500,
                '& strong': {
                  color: '#1976d2',
                  fontWeight: 600
                }
              }}>
                Complete the form with your personal and professional details and upload the required documents. This helps us set up your records and ensure a smooth start to your journey with us.
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 600, fontSize: '22px' }}>
              Employee Onboarding Form
            </Typography>
          {/* Personal Information Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              Personal Information
            </Typography>
            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Employee ID"
                  name="employeeId"
                  fullWidth
                  value={values.employeeId || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.employeeId && Boolean(errors.employeeId)}
                  helperText={touched.employeeId && errors.employeeId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name of Employee"
                  name="employeeName"
                  fullWidth
                  value={values.employeeName || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.employeeName && Boolean(errors.employeeName)}
                  helperText={touched.employeeName && errors.employeeName}
                />
              </Grid>
              
              {/* Row 2 */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={touched.sex && Boolean(errors.sex)}>
                  <InputLabel>Sex</InputLabel>
                  <Select
                    name="sex"
                    value={values.sex || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Sex"
                  >
                    <MenuItem value="">Select Sex</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                  {touched.sex && errors.sex && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.sex}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Joining"
                  name="dateOfJoining"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={values.dateOfJoining || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.dateOfJoining && Boolean(errors.dateOfJoining)}
                  helperText={touched.dateOfJoining && errors.dateOfJoining}
                />
              </Grid>
              
              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={touched.country && Boolean(errors.country)}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    name="country"
                    value={values.country || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Country"
                  >
                    <MenuItem value="">Select Country</MenuItem>
                    <MenuItem value="India">India</MenuItem>
                    <MenuItem value="Vietnam">Vietnam</MenuItem>
                    <MenuItem value="Indonesia">Indonesia</MenuItem>
                  </Select>
                  {touched.country && errors.country && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.country}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nationality"
                  name="nationality"
                  fullWidth
                  value={values.nationality || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.nationality && Boolean(errors.nationality)}
                  helperText={touched.nationality && errors.nationality}
                />
              </Grid>
              
              {/* Row 4 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={values.dateOfBirth || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                  helperText={touched.dateOfBirth && errors.dateOfBirth}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={touched.maritalStatus && Boolean(errors.maritalStatus)}>
                  <InputLabel>Marital Status</InputLabel>
                  <Select
                    name="maritalStatus"
                    value={values.maritalStatus || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Marital Status"
                  >
                    <MenuItem value="">Select Marital Status</MenuItem>
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Divorced">Divorced</MenuItem>
                    <MenuItem value="Widowed">Widowed</MenuItem>
                  </Select>
                  {touched.maritalStatus && errors.maritalStatus && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.maritalStatus}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              {/* Row 5 */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={touched.bloodGroup && Boolean(errors.bloodGroup)}>
                  <InputLabel>Blood Gr.</InputLabel>
                  <Select
                    name="bloodGroup"
                    value={values.bloodGroup || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Blood Gr."
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                  {touched.bloodGroup && errors.bloodGroup && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.bloodGroup}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reporting Manager"
                  name="reportingManager"
                  fullWidth
                  value={values.reportingManager || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  required
                  error={touched.reportingManager && Boolean(errors.reportingManager)}
                  helperText={touched.reportingManager && errors.reportingManager}
                />
              </Grid>
              
              {/* Row 6 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Unit Head"
                  name="unitHead"
                  fullWidth
                  value={values.unitHead || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.unitHead && Boolean(errors.unitHead)}
                  helperText={touched.unitHead && errors.unitHead}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Address Section */}
          <Box sx={{ mb: 4 }}>
            {/* <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              ADDRESS INFORMATION
            </Typography> */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '18px' }}>
              Current Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Street"
                  name="currentAddress.street"
                  fullWidth
                  value={safeValues.currentAddress.street || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.currentAddress?.street && Boolean(errors.currentAddress?.street)}
                  helperText={touched.currentAddress?.street && errors.currentAddress?.street}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  name="currentAddress.city"
                  fullWidth
                  value={safeValues.currentAddress.city || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.currentAddress?.city && Boolean(errors.currentAddress?.city)}
                  helperText={touched.currentAddress?.city && errors.currentAddress?.city}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={values.country === 'India' ? 'State' : values.country === 'Indonesia' || values.country === 'Vietnam' ? 'Province' : 'State/Province'}
                  name="currentAddress.state"
                  fullWidth
                  value={safeValues.currentAddress.state || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.currentAddress?.state && Boolean(errors.currentAddress?.state)}
                  helperText={touched.currentAddress?.state && errors.currentAddress?.state}
                />
            </Grid>
              <Grid item xs={12} sm={6}>
                  <TextField
                  label={values.country === 'India' ? 'Pin-code' : values.country === 'Indonesia' || values.country === 'Vietnam' ? 'Postal Code' : 'Pin-code/ZIP Code/Postal Code'}
                  name="currentAddress.zipCode"
                    fullWidth
                  value={safeValues.currentAddress.zipCode || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  error={touched.currentAddress?.zipCode && Boolean(errors.currentAddress?.zipCode)}
                  helperText={touched.currentAddress?.zipCode && errors.currentAddress?.zipCode}
                  />
                </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country"
                  name="currentAddress.country"
                  fullWidth
                  value={safeValues.currentAddress.country || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.currentAddress?.country && Boolean(errors.currentAddress?.country)}
                  helperText={touched.currentAddress?.country && errors.currentAddress?.country}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={safeValues.permanentAddress.sameAsCurrent || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFieldValue('permanentAddress.sameAsCurrent', checked);
                    if (checked) {
                      setFieldValue('permanentAddress', {
                        ...safeValues.currentAddress,
                        sameAsCurrent: true,
                      });
                    }
                  }}
                  disabled={dataLoading}
                />
              }
              label="Permanent address same as current"
            />

            {!safeValues.permanentAddress.sameAsCurrent && (
              <>
                <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600, fontSize: '18px' }}>
                  Permanent Address
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                      <TextField
                      label="Street"
                      name="permanentAddress.street"
                        fullWidth
                      value={safeValues.permanentAddress.street || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      error={touched.permanentAddress?.street && Boolean(errors.permanentAddress?.street)}
                      helperText={touched.permanentAddress?.street && errors.permanentAddress?.street}
                      />
                    </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="City"
                      name="permanentAddress.city"
                      fullWidth
                      value={safeValues.permanentAddress.city || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      error={touched.permanentAddress?.city && Boolean(errors.permanentAddress?.city)}
                      helperText={touched.permanentAddress?.city && errors.permanentAddress?.city}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={values.country === 'India' ? 'State' : values.country === 'Indonesia' || values.country === 'Vietnam' ? 'Province' : 'State/Province'}
                      name="permanentAddress.state"
                      fullWidth
                      value={safeValues.permanentAddress.state || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                                        error={touched.permanentAddress?.state && Boolean(errors.permanentAddress?.state)}
                  helperText={touched.permanentAddress?.state && errors.permanentAddress?.state}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={values.country === 'India' ? 'Pin-code' : values.country === 'Indonesia' || values.country === 'Vietnam' ? 'Postal Code' : 'Pin-code/ZIP Code/Postal Code'}
                      name="permanentAddress.zipCode"
                      fullWidth
                      value={safeValues.permanentAddress.zipCode || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      error={touched.permanentAddress?.zipCode && Boolean(errors.permanentAddress?.zipCode)}
                      helperText={touched.permanentAddress?.zipCode && errors.permanentAddress?.zipCode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Country"
                      name="permanentAddress.country"
                      fullWidth
                      value={safeValues.permanentAddress.country || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      error={touched.permanentAddress?.country && Boolean(errors.permanentAddress?.country)}
                      helperText={touched.permanentAddress?.country && errors.permanentAddress?.country}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          {/* Contact Information Section */}
          <Box sx={{ mb: 4 }}>
            {/* <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontWeight: 'bold' }}>
              CONTACT INFORMATION
            </Typography> */}
            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email ID"
                  name="emailId"
                  type="email"
                  fullWidth
                  value={values.emailId || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.emailId && Boolean(errors.emailId)}
                  helperText={touched.emailId && errors.emailId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={
                    values.country === 'India' 
                      ? 'Pan Card No.' 
                      : values.country === 'Indonesia' 
                        ? 'Tax Identification No.' 
                        : values.country === 'Vietnam' 
                          ? 'Tax Identification No.' 
                          : 'Tax Identification No.'
                  }
                  name={values.country === 'India' ? 'panCardNumber' : 'taxIdentificationNumber'}
                  fullWidth
                  value={values.country === 'India' ? (values.panCardNumber || '') : (values.taxIdentificationNumber || '')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={
                    values.country === 'India' 
                      ? (touched.panCardNumber && Boolean(errors.panCardNumber))
                      : (touched.taxIdentificationNumber && Boolean(errors.taxIdentificationNumber))
                  }
                  helperText={
                    values.country === 'India' 
                      ? (touched.panCardNumber && errors.panCardNumber)
                      : (touched.taxIdentificationNumber && errors.taxIdentificationNumber)
                  }
                  placeholder={
                    values.country === 'India' 
                      ? 'ABCDE1234F' 
                      : values.country === 'Indonesia' 
                        ? 'Enter Indonesia Tax ID' 
                        : values.country === 'Vietnam' 
                          ? 'Enter Vietnam Tax ID' 
                          : 'Enter Tax ID'
                  }
                />
              </Grid>
              
              {/* Row 2 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={
                    values.country === 'India' 
                      ? 'Aadhar No.' 
                      : values.country === 'Indonesia' 
                        ? 'National Identity No.' 
                        : values.country === 'Vietnam' 
                          ? 'National Identity No.' 
                          : 'National Identity No.'
                  }
                  name={values.country === 'India' ? 'aadharNumber' : 'nationalIdentityNumber'}
                  fullWidth
                  value={values.country === 'India' ? (values.aadharNumber || '') : (values.nationalIdentityNumber || '')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={
                    values.country === 'India' 
                      ? (touched.aadharNumber && Boolean(errors.aadharNumber))
                      : (touched.nationalIdentityNumber && Boolean(errors.nationalIdentityNumber))
                  }
                  helperText={
                    values.country === 'India' 
                      ? (touched.aadharNumber && errors.aadharNumber)
                      : (touched.nationalIdentityNumber && errors.nationalIdentityNumber)
                  }
                  placeholder={
                    values.country === 'India' 
                      ? '123456789012' 
                      : values.country === 'Indonesia' 
                        ? 'Enter Indonesia National ID' 
                        : values.country === 'Vietnam' 
                          ? 'Enter Vietnam National ID' 
                          : 'Enter National ID'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    backgroundColor: '#f5f5f5', 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    px: 2, 
                    py: 1.5,
                    minWidth: 80,
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}>
                    {values.country === 'India' ? '+91' : values.country === 'Indonesia' ? '+62' : values.country === 'Vietnam' ? '+84' : '+XX'}
                  </Box>
                  <TextField
                    label="Mobile Number"
                    name="mobileNumber"
                    fullWidth
                    value={values.mobileNumber || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={touched.mobileNumber && Boolean(errors.mobileNumber)}
                    helperText={touched.mobileNumber && errors.mobileNumber}
                    placeholder="Enter mobile number"
                  />
                </Box>
            </Grid>

              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                  <TextField
                  label="Emergency Contact & Relations"
                  name="emergencyContact.name"
                    fullWidth
                  value={safeValues.emergencyContact.name || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  error={touched.emergencyContact?.name && Boolean(errors.emergencyContact?.name)}
                  helperText={touched.emergencyContact?.name && errors.emergencyContact?.name}
                  placeholder="Enter emergency contact name"
                  />
                </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Emergency Contact Number"
                  name="emergencyContact.contactNo"
                  fullWidth
                  value={safeValues.emergencyContact.contactNo || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.emergencyContact?.contactNo && Boolean(errors.emergencyContact?.contactNo)}
                  helperText={touched.emergencyContact?.contactNo && errors.emergencyContact?.contactNo}
                  placeholder="Enter emergency contact number"
                />
            </Grid>
              
              {/* Row 4 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Relationship with Employee"
                  name="emergencyContact.relationshipWithEmployee"
                  fullWidth
                  value={safeValues.emergencyContact.relationshipWithEmployee || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.emergencyContact?.relationshipWithEmployee && Boolean(errors.emergencyContact?.relationshipWithEmployee)}
                  helperText={touched.emergencyContact?.relationshipWithEmployee && errors.emergencyContact?.relationshipWithEmployee}
                  placeholder="e.g., Brother, Father, Spouse"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Passport No."
                  name="passportNo"
                  fullWidth
                  value={values.passportNo || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.passportNo && Boolean(errors.passportNo)}
                  helperText={touched.passportNo && errors.passportNo}
                  placeholder="Enter passport number (optional)"
                />
            </Grid>
            </Grid>


          </Box>

          {/* Work Experience Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              Work Experience / Profile
            </Typography>
            <FieldArray name="workExperiences">
              {({ push, remove }) => (
                <>
                  {safeValues.workExperiences.map((entry, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '18px' }}>
                        Work Experience {index + 1}
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name={`workExperiences[${index}].organizationName`}
                            label="Organization Name"
                            fullWidth
                            value={entry.organizationName || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.organizationName && Boolean(errors.workExperiences?.[index]?.organizationName)}
                            helperText={touched.workExperiences?.[index]?.organizationName && errors.workExperiences?.[index]?.organizationName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name={`workExperiences[${index}].designation`}
                            label="Designation"
                            fullWidth
                            value={entry.designation || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.designation && Boolean(errors.workExperiences?.[index]?.designation)}
                            helperText={touched.workExperiences?.[index]?.designation && errors.workExperiences?.[index]?.designation}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name={`workExperiences[${index}].fromDate`}
                            label="From Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={entry.fromDate || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.fromDate && Boolean(errors.workExperiences?.[index]?.fromDate)}
                            helperText={touched.workExperiences?.[index]?.fromDate && errors.workExperiences?.[index]?.fromDate}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name={`workExperiences[${index}].toDate`}
                            label="To Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={entry.toDate || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.toDate && Boolean(errors.workExperiences?.[index]?.toDate)}
                            helperText={touched.workExperiences?.[index]?.toDate && errors.workExperiences?.[index]?.toDate}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name={`workExperiences[${index}].contactNumber`}
                            label="Contact Number"
                            fullWidth
                            value={entry.contactNumber || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.contactNumber && Boolean(errors.workExperiences?.[index]?.contactNumber)}
                            helperText={touched.workExperiences?.[index]?.contactNumber && errors.workExperiences?.[index]?.contactNumber}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name={`workExperiences[${index}].jobDescription`}
                            label="Job Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={entry.jobDescription || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.workExperiences?.[index]?.jobDescription && Boolean(errors.workExperiences?.[index]?.jobDescription)}
                            helperText={touched.workExperiences?.[index]?.jobDescription && errors.workExperiences?.[index]?.jobDescription}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => remove(index)}
                            >
                              Remove Experience
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        push({
                          organizationName: '',
                          designation: '',
                          fromDate: '',
                          toDate: '',
                          contactNumber: '',
                          jobDescription: '',
                        })
                      }
                    >
                      Add Another Work Experience
                    </Button>
                  </Box>
                </>
              )}
            </FieldArray>
          </Box>

          {/* Family Members Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              Family Member Details
            </Typography>
            <FieldArray name="familyMembers">
              {({ push, remove }) => (
                <>
                  {safeValues.familyMembers.map((member, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '18px' }}>
                        Family Member {index + 1}
                      </Typography>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name={`familyMembers[${index}].name`}
                            label="Name"
                            fullWidth
                            value={member.name || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.familyMembers?.[index]?.name && Boolean(errors.familyMembers?.[index]?.name)}
                            helperText={touched.familyMembers?.[index]?.name && errors.familyMembers?.[index]?.name}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name={`familyMembers[${index}].dateOfBirth`}
                            label="Date of Birth"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={member.dateOfBirth || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.familyMembers?.[index]?.dateOfBirth && Boolean(errors.familyMembers?.[index]?.dateOfBirth)}
                            helperText={touched.familyMembers?.[index]?.dateOfBirth && errors.familyMembers?.[index]?.dateOfBirth}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <FormControl fullWidth>
                            <InputLabel>Relation</InputLabel>
                            <Select
                              name={`familyMembers[${index}].relation`}
                              value={member.relation || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Relation"
                            >
                              <MenuItem value="">Select Relation</MenuItem>
                              <MenuItem value="Father">Father</MenuItem>
                              <MenuItem value="Mother">Mother</MenuItem>
                              <MenuItem value="Brother">Brother</MenuItem>
                              <MenuItem value="Sister">Sister</MenuItem>
                              <MenuItem value="Spouse">Spouse</MenuItem>
                              <MenuItem value="Son">Son</MenuItem>
                              <MenuItem value="Daughter">Daughter</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name={`familyMembers[${index}].residingWithSelf`}
                                checked={member.residingWithSelf || false}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                            label="Residing with self"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => remove(index)}
                            >
                              Remove Member
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        push({
                          name: '',
                          dateOfBirth: '',
                          relation: '',
                          residingWithSelf: false,
                        })
                      }
                    >
                      Add Another Family Member
                    </Button>
                  </Box>
                </>
              )}
            </FieldArray>
          </Box>

          {/* Bank Details Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              Bank Details
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, fontSize: '16px' }}>
              (Please support along with a Cancelled Cheque with printed name or bank statements/passbook - front page, with details mentioned below)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Name"
                  name="bankDetails.bankName"
                  fullWidth
                  value={safeValues.bankDetails.bankName || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.bankDetails?.bankName && Boolean(errors.bankDetails?.bankName)}
                  helperText={touched.bankDetails?.bankName && errors.bankDetails?.bankName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Branch Address"
                  name="bankDetails.branchAddress"
                  fullWidth
                  value={safeValues.bankDetails.branchAddress || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.bankDetails?.branchAddress && Boolean(errors.bankDetails?.branchAddress)}
                  helperText={touched.bankDetails?.branchAddress && errors.bankDetails?.branchAddress}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account Name"
                  name="bankDetails.accountName"
                  fullWidth
                  value={safeValues.bankDetails.accountName || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.bankDetails?.accountName && Boolean(errors.bankDetails?.accountName)}
                  helperText={touched.bankDetails?.accountName && errors.bankDetails?.accountName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account No"
                  name="bankDetails.accountNo"
                  fullWidth
                  value={safeValues.bankDetails.accountNo || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.bankDetails?.accountNo && Boolean(errors.bankDetails?.accountNo)}
                  helperText={touched.bankDetails?.accountNo && errors.bankDetails?.accountNo}
                />
              </Grid>
              {values.country === 'India' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="IFSC Code"
                  name="bankDetails.ifscCode"
                  fullWidth
                  value={safeValues.bankDetails.ifscCode || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={touched.bankDetails?.ifscCode && Boolean(errors.bankDetails?.ifscCode)}
                  helperText={touched.bankDetails?.ifscCode && errors.bankDetails?.ifscCode}
                  placeholder="ABCD0123456"
                />
              </Grid>
              )}

              {(values.country === 'Indonesia' || values.country === 'Vietnam') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Swift Code/Bank Code"
                    name="bankDetails.swiftCode"
                    fullWidth
                    value={safeValues.bankDetails.swiftCode || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={touched.bankDetails?.swiftCode && Boolean(errors.bankDetails?.swiftCode)}
                    helperText={touched.bankDetails?.swiftCode && errors.bankDetails?.swiftCode}
                    placeholder="Enter Swift Code or Bank Code"
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* PF Details Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              PF Details / Social Security Details
            </Typography>
            
            <Grid container spacing={3}>
              {values.country === 'India' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="UAN No. (if applicable)"
                    name="pfDetails.uanNumber"
                    fullWidth
                    value={safeValues.pfDetails.uanNumber || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.pfDetails?.uanNumber && Boolean(errors.pfDetails?.uanNumber)}
                    helperText={touched.pfDetails?.uanNumber && errors.pfDetails?.uanNumber}
                    placeholder="Enter 12-digit UAN number"
                  />
                </Grid>
              )}

              {(values.country === 'Indonesia' || values.country === 'Vietnam') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Social Security No. (NSM)"
                    name="pfDetails.socialSecurityNumber"
                    fullWidth
                    value={safeValues.pfDetails.socialSecurityNumber || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.pfDetails?.socialSecurityNumber && Boolean(errors.pfDetails?.socialSecurityNumber)}
                    helperText={touched.pfDetails?.socialSecurityNumber && errors.pfDetails?.socialSecurityNumber}
                    placeholder={`Enter ${values.country} Social Security Number`}
                    />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Declaration Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1, fontSize: '18px', fontWeight: 600 }}>
              Declaration
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Checkbox
                name="declaration"
                checked={values.declaration || false}
                onChange={(e) => {
                  setFieldValue('declaration', e.target.checked);
                }}
                onBlur={handleBlur}
                required
                sx={{
                  mt: 0.5,
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
                disabled={dataLoading}
              />
              <Typography variant="body1" sx={{ lineHeight: 1.6, textAlign: 'justify', fontSize: '16px' }}>
                I declare that the information given in this personal data form and the certificates accompanying it is correct and complete to best of my knowledge and belief. I also understand that at any stage I may be asked to provide adequate justification of the facts stated above, and I would do so when called for. I accept the job and position given to me in all respect and will strive to work towards company core values and help to achieve its vision and mission.
              </Typography>
            </Box>
            {touched.declaration && !values.declaration && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                You must accept the declaration to proceed.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />
          
          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              type="submit" 
              variant="contained" 
              size="medium"
              disabled={loading || !values.declaration || dataLoading}
              sx={{ minWidth: 200, py: 1.5, fontSize: '14px', fontWeight: 600 }}
            >
              {loading ? 'Saving...' : (isUpdate ? 'Update All Information' : 'Save All Information')}
            </Button>
          </Box>
        </form>
      )}
      </Paper>
    </FormikProvider>
  );
};

export default FormsStep;
